/**
 * Праздники бирж Токио, Лондона и Нью-Йорка.
 *
 * Сессия — это часы, когда работает биржа. В праздник биржа закрыта, и
 * «азиатская сессия» такого дня — просто ночь: объёма Токио в ней нет.
 * Без календаря индикатор «Сессии» рисовал Азию посреди японских
 * «серебряных каникул», а «Главная» обещала открытие Нью-Йорка в День
 * благодарения.
 *
 * Календарь считается по правилам, а не хранится таблицей: правила
 * праздников меняются раз в десятилетия, а таблица устаревает каждый
 * январь. Разовые закрытия (траур, коронация) по правилам не
 * предсказать — они перечислены отдельно, по мере объявления.
 *
 * Даты — местные, в часовом поясе биржи. Выходные сюда не попадают: в
 * субботу и воскресенье биржи закрыты и без праздника.
 */
/** Город биржи словами — для подписей «праздник в Токио». */
export const EXCHANGE_CITY = {
    tokyo: { name: 'Токио', where: 'в Токио' },
    london: { name: 'Лондон', where: 'в Лондоне' },
    'new-york': { name: 'Нью-Йорк', where: 'в Нью-Йорке' },
};
const DAY_MS = 86_400_000;
function key(y, m, d) {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
/** День недели: 0 — воскресенье, 6 — суббота. */
function dow(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
function shift(day, by) {
    const at = new Date(Date.UTC(day.y, day.m - 1, day.d) + by * DAY_MS);
    return { y: at.getUTCFullYear(), m: at.getUTCMonth() + 1, d: at.getUTCDate() };
}
/** N-й такой-то день недели месяца: `nth(2026, 1, 1, 3)` — третий понедельник января. */
function nth(y, m, weekday, n) {
    const first = dow(y, m, 1);
    return { y, m, d: 1 + ((weekday - first + 7) % 7) + (n - 1) * 7 };
}
/** Последний такой-то день недели месяца. */
function lastOf(y, m, weekday) {
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const lastDow = dow(y, m, days);
    return { y, m, d: days - ((lastDow - weekday + 7) % 7) };
}
/** Пасха по григорианскому календарю (анонимный алгоритм). */
function easter(y) {
    const a = y % 19;
    const b = Math.floor(y / 100);
    const c = y % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { y, m: month, d: day };
}
/* ------------------------------------------------------------ Токио */
/**
 * Равноденствия по формуле Японского астрономического управления —
 * верна для 1980–2099 годов. Точную дату правительство объявляет за
 * год, и она с формулой совпадает.
 */
function vernal(y) {
    return Math.floor(20.8431 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
}
function autumnal(y) {
    return Math.floor(23.2488 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
}
function tokyo(y) {
    // Государственные праздники по закону о национальных праздниках.
    const base = new Map();
    const put = (day, name) => {
        base.set(key(day.y, day.m, day.d), name);
    };
    put({ y, m: 1, d: 1 }, 'Новый год');
    put(nth(y, 1, 1, 2), 'День совершеннолетия');
    put({ y, m: 2, d: 11 }, 'День основания государства');
    put({ y, m: 2, d: 23 }, 'День рождения императора');
    put({ y, m: 3, d: vernal(y) }, 'День весеннего равноденствия');
    put({ y, m: 4, d: 29 }, 'День Сёва');
    put({ y, m: 5, d: 3 }, 'День конституции');
    put({ y, m: 5, d: 4 }, 'День зелени');
    put({ y, m: 5, d: 5 }, 'День детей');
    put(nth(y, 7, 1, 3), 'День моря');
    put({ y, m: 8, d: 11 }, 'День гор');
    put(nth(y, 9, 1, 3), 'День почитания старших');
    put({ y, m: 9, d: autumnal(y) }, 'День осеннего равноденствия');
    put(nth(y, 10, 1, 2), 'День спорта');
    put({ y, m: 11, d: 3 }, 'День культуры');
    put({ y, m: 11, d: 23 }, 'День благодарности труду');
    const all = new Map(base);
    // «Народный выходной»: будний день, зажатый между двумя праздниками.
    for (let m = 1; m <= 12; m += 1) {
        const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
        for (let d = 1; d <= days; d += 1) {
            const today = { y, m, d };
            const before = shift(today, -1);
            const after = shift(today, 1);
            if (!base.has(key(y, m, d)) &&
                dow(y, m, d) !== 0 &&
                base.has(key(before.y, before.m, before.d)) &&
                base.has(key(after.y, after.m, after.d))) {
                all.set(key(y, m, d), 'Народный выходной между праздниками');
            }
        }
    }
    // Праздник пришёлся на воскресенье — выходным становится ближайший
    // следующий день, который сам не праздник.
    for (const [date, name] of base) {
        const [yy, mm, dd] = date.split('-').map(Number);
        if (dow(yy, mm, dd) !== 0)
            continue;
        let next = shift({ y: yy, m: mm, d: dd }, 1);
        while (all.has(key(next.y, next.m, next.d)))
            next = shift(next, 1);
        all.set(key(next.y, next.m, next.d), `${name} (перенос)`);
    }
    // Биржа закрыта ещё и в первые три дня года и в последний.
    all.set(key(y, 1, 2), all.get(key(y, 1, 2)) ?? 'Новогодние каникулы');
    all.set(key(y, 1, 3), all.get(key(y, 1, 3)) ?? 'Новогодние каникулы');
    all.set(key(y, 12, 31), 'Последний день года');
    return [...all].map(([date, name]) => ({ exchange: 'tokyo', date, name, kind: 'closed' }));
}
/* ----------------------------------------------------------- Лондон */
function london(y) {
    const out = [];
    const closed = (day, name) => {
        out.push({ exchange: 'london', date: key(day.y, day.m, day.d), name, kind: 'closed' });
    };
    // Новый год: выпал на выходной — отдыхают в ближайший понедельник.
    const newYear = dow(y, 1, 1);
    closed({ y, m: 1, d: newYear === 6 ? 3 : newYear === 0 ? 2 : 1 }, 'Новый год');
    const east = easter(y);
    closed(shift(east, -2), 'Страстная пятница');
    closed(shift(east, 1), 'Пасхальный понедельник');
    closed(nth(y, 5, 1, 1), 'Майский банковский выходной');
    closed(lastOf(y, 5, 1), 'Весенний банковский выходной');
    closed(lastOf(y, 8, 1), 'Летний банковский выходной');
    // Рождество и День подарков с переносами на понедельник и вторник.
    const christmas = dow(y, 12, 25);
    if (christmas === 6) {
        closed({ y, m: 12, d: 27 }, 'Рождество (перенос)');
        closed({ y, m: 12, d: 28 }, 'День подарков (перенос)');
    }
    else if (christmas === 0) {
        closed({ y, m: 12, d: 26 }, 'День подарков');
        closed({ y, m: 12, d: 27 }, 'Рождество (перенос)');
    }
    else if (christmas === 5) {
        closed({ y, m: 12, d: 25 }, 'Рождество');
        closed({ y, m: 12, d: 28 }, 'День подарков (перенос)');
    }
    else {
        closed({ y, m: 12, d: 25 }, 'Рождество');
        closed({ y, m: 12, d: 26 }, 'День подарков');
    }
    // Канун Рождества и Нового года — торги до 12:30.
    for (const [d, name] of [
        [24, 'Сочельник'],
        [31, 'Канун Нового года'],
    ]) {
        const weekday = dow(y, 12, d);
        const date = key(y, 12, d);
        if (weekday === 0 || weekday === 6 || out.some((entry) => entry.date === date))
            continue;
        out.push({ exchange: 'london', date, name, kind: 'short', close: [12, 30] });
    }
    return out;
}
/* --------------------------------------------------------- Нью-Йорк */
/** Разовые закрытия NYSE, которые по правилам не вычислить. */
const NYSE_SPECIAL = [
    { exchange: 'new-york', date: '2025-01-09', name: 'День траура по Джимми Картеру', kind: 'closed' },
];
function newYork(y) {
    const out = [];
    const closed = (day, name) => {
        out.push({ exchange: 'new-york', date: key(day.y, day.m, day.d), name, kind: 'closed' });
    };
    /** Выпал на субботу — отдыхают в пятницу, на воскресенье — в понедельник. */
    const observed = (m, d) => {
        const weekday = dow(y, m, d);
        return shift({ y, m, d }, weekday === 6 ? -1 : weekday === 0 ? 1 : 0);
    };
    // Новый год в субботу NYSE не переносит: пятница 31 декабря — торговый день.
    if (dow(y, 1, 1) !== 6)
        closed(observed(1, 1), 'Новый год');
    closed(nth(y, 1, 1, 3), 'День Мартина Лютера Кинга');
    closed(nth(y, 2, 1, 3), 'День президентов');
    closed(shift(easter(y), -2), 'Страстная пятница');
    closed(lastOf(y, 5, 1), 'День памяти');
    if (y >= 2022)
        closed(observed(6, 19), 'День освобождения (Джунтин)');
    closed(observed(7, 4), 'День независимости');
    closed(nth(y, 9, 1, 1), 'День труда');
    const thanksgiving = nth(y, 11, 4, 4);
    closed(thanksgiving, 'День благодарения');
    closed(observed(12, 25), 'Рождество');
    // Сокращённые дни — торги до 13:00.
    const short = (day, name) => {
        const date = key(day.y, day.m, day.d);
        const weekday = dow(day.y, day.m, day.d);
        if (weekday === 0 || weekday === 6 || out.some((entry) => entry.date === date))
            return;
        out.push({ exchange: 'new-york', date, name, kind: 'short', close: [13, 0] });
    };
    // Канун Дня независимости — только когда сам праздник выпал на будний день.
    const july4 = dow(y, 7, 4);
    if (july4 >= 2 && july4 <= 5)
        short({ y, m: 7, d: 3 }, 'Канун Дня независимости');
    short(shift(thanksgiving, 1), 'Чёрная пятница');
    short({ y, m: 12, d: 24 }, 'Сочельник');
    return [...out, ...NYSE_SPECIAL.filter((entry) => entry.date.startsWith(`${y}-`))];
}
/* ------------------------------------------------------------ опрос */
const cache = new Map();
/** Все праздники и сокращённые дни биржи за год. */
export function holidaysOf(exchange, year) {
    const id = `${exchange}|${year}`;
    const known = cache.get(id);
    if (known)
        return known;
    const list = exchange === 'tokyo' ? tokyo(year) : exchange === 'london' ? london(year) : newYork(year);
    // Только будни: в выходные биржа закрыта и без праздника.
    const map = new Map(list
        .filter((entry) => {
        const [yy, mm, dd] = entry.date.split('-').map(Number);
        const weekday = dow(yy, mm, dd);
        return weekday !== 0 && weekday !== 6;
    })
        .map((entry) => [entry.date, entry]));
    cache.set(id, map);
    return map;
}
/** Праздник или сокращённый день биржи в эту местную дату. */
export function holidayOn(exchange, y, m, d) {
    return holidaysOf(exchange, y).get(key(y, m, d)) ?? null;
}
//# sourceMappingURL=holidays.js.map